/**
 * CustomAlert Component
 * 
 * Displays an animated alert message with customizable type, message content, 
 * and duration. The alert will automatically slide in and out based on the specified duration.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.message - The message to display in the alert.
 * @param {"error" | "warning" | "info" | "success"} [props.type="success"] - Optional. The type of alert. Defaults to "success".
 * @param {number} [props.duration=3000] - Optional. Duration in milliseconds before the alert slides out. Defaults to 3000 ms.
 * 
 * @example
 * // Basic usage
 * <CustomAlert 
 *    message="Operation successful!" 
 *    type="success" 
 *    duration={3000} 
 * />
 */

import React, { useState, useEffect } from "react";
import Alert from '@mui/material/Alert';
import useAnimation from "../services/functions/useAnimation";


interface CustomAlertProps {
    message: string;
    type?: "error" | "warning" | "info" | "success";
    duration?: number;
}

const CustomAlert: React.FC<CustomAlertProps> = ({
    message,
    type = 'info',
    duration = 5000,
}) => {
    const { slideIn, slideOutAll } = useAnimation()
    const [appear, setAppear] = useState<Boolean>(true);

    useEffect(() => {
        slideIn(".MuiPaper-root");

        const slideOutTimeout = setTimeout(() => {
            slideOutAll(".MuiPaper-root");

            setTimeout(() => {
                setAppear(false);
            }, 700);
        }, duration);

        // Cleanup timeout on unmount
        return () => clearTimeout(slideOutTimeout);
    }, [duration, slideIn, slideOutAll]);

    return (
        appear &&
        <Alert variant="filled" severity={type}>
            {message}
        </Alert>
    );
};

export default CustomAlert;